/**
 * AI Regulatory program tabs — V98-4-5, hosted by ProgramShell at
 * /programs/ai-regulatory/:tab (D-98-2: its numbers never mix with SOC 2 /
 * FedRAMP). V98-4 review fixes noted inline.
 *
 *   Scope    obligation sources (the toggle as a versioned Legal decision,
 *            the org ceiling locked) + the decision vN editor (Legal) +
 *            the laws × systems grid + the program boundary.
 *            `?cell=<id>` opens that cell (deep link from Today / work items).
 *   Operate  "Undecided in force" (the server's in-force gap cells — the same
 *            number Today and Programs show, T4-H1), "Non-compliant"
 *            (remediation), interim positions, and the in-force laws that apply.
 *   Audit    "Conformity" — derived from the EU risk tiers (never stored).
 *
 * After every write the program, Today and `/me/counts` refetch, so the
 * counts decrement everywhere from the one server counter.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, EmptyState, Spinner } from '@aigovops/design-system';
import type { ProgramCard } from '@aigovops/core';
import { getAiGrid, getObligationSources, type AiGrid } from '../../lib/ai-program-api';
import { listBlockers } from '../../lib/coverage-api';
import { cellDisplay, cellTitle, operateSplit } from '../../lib/ai-program-model';
import { invalidationMap } from '../../lib/query-keys';
import { fmtDay, itemHref, type Names } from '../../lib/today-model';
import { BoundaryPanel } from '../programs/BoundaryPanel';
import { Chip, useAnnounce, useFocusAfter } from '../today/shared';
import { ApplicabilityGrid, CellEditor, cellButtonId } from './ApplicabilityGrid';
import {
  AskLegalDialog, AskPolicyDialog, DecisionEditor, ObligationSourcesCard, ProposeDialog, ReleaseDialog, ReleasesDialog, releasesKey, useReleases,
} from './ObligationSources';
import { DecisionsEntry } from './ContestedDecisions';
import './ai-program.css';

export const aiKeys = {
  grid: (slug: string) => ['ai-grid', slug] as const,
  sources: (slug: string) => ['ai-sources', slug] as const,
};

/** The grid (any member; outcomes only unless Legal) — also used by the shell for the Conformity station. */
export function useAiGrid(slug: string, enabled = true) {
  return useQuery({ queryKey: aiKeys.grid(slug), queryFn: () => getAiGrid(slug), enabled, staleTime: 30_000, retry: false });
}

function useRefresh(slug: string) {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: aiKeys.grid(slug) }),
      qc.invalidateQueries({ queryKey: aiKeys.sources(slug) }),
      qc.invalidateQueries({ queryKey: ['ai-cell-history', slug] }),
      qc.invalidateQueries({ queryKey: releasesKey(slug) }),
      ...invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })),
      qc.invalidateQueries({ queryKey: ['blockers', slug] }),
    ]);
  };
}

export function AiScope({ slug, card, canManage, isAdmin, viewerId, names }: {
  slug: string; card: ProgramCard; canManage: boolean; isAdmin: boolean; viewerId: string; names: Names;
}) {
  const grid = useAiGrid(slug);
  const sources = useQuery({ queryKey: aiKeys.sources(slug), queryFn: () => getObligationSources(slug), staleTime: 30_000, retry: false });
  const refresh = useRefresh(slug);
  const [params, setParams] = useSearchParams();
  const [cellId, setCellId] = useState<string | null>(null);
  const [returnTo, setReturnTo] = useState<string | null>(null);
  const [editor, setEditor] = useState(false);
  const [propose, setPropose] = useState(false);
  const [release, setRelease] = useState<{ preselect: string | null } | null>(null);
  const [releasesOpen, setReleasesOpen] = useState(false);
  const [askLegal, setAskLegal] = useState(false);
  const [askPolicy, setAskPolicy] = useState(false);
  const [live, announce] = useAnnounce();
  const focusLater = useFocusAfter();
  const isLegal = !!sources.data?.can_decide;
  const releases = useReleases(slug, isLegal);

  // Deep links: ?cell=<id> opens that cell once the grid is in; ?record=1 opens the decision editor (Legal Today).
  useEffect(() => {
    const id = params.get('cell');
    if (id && grid.data?.cells.some((c) => c.cell_id === id)) { setCellId(id); setReturnTo(cellButtonId(id)); }
  }, [params, grid.data]);
  useEffect(() => {
    if (params.get('record') === '1' && sources.data?.can_decide) {
      setEditor(true);
      params.delete('record');
      setParams(params, { replace: true });
    }
  }, [params, sources.data, setParams]);
  const openCell = (id: string, back: string) => { setCellId(id); setReturnTo(back); };
  // T4-H4: focus goes back where the cell was opened from (the grid cell, or the next Decide button in the draft).
  const closeCell = () => {
    const back = returnTo;
    setCellId(null);
    if (params.get('cell')) { params.delete('cell'); setParams(params, { replace: true }); }
    if (back) focusLater(back);
  };
  const done = async (m: string, after?: () => void) => {
    await refresh();
    after?.();
    if (m) announce(m);
  };

  if (grid.isLoading || sources.isLoading) return <Spinner label="Loading AI Regulatory scope" />;
  if (grid.error || sources.error || !grid.data || !sources.data) {
    return <Alert variant="error"><p>The AI Regulatory scope could not load.</p><Button onClick={() => { void grid.refetch(); void sources.refetch(); }}>Retry</Button></Alert>;
  }
  const g = grid.data;
  const s = sources.data;
  const cell = cellId ? g.cells.find((c) => c.cell_id === cellId) ?? null : null;
  const legalName = s.legal_reviewer?.name ?? null;
  const locked = !s.ceiling.allows_legislation;
  const contact = s.policy_contact?.name ?? 'the org admin';
  return (
    <div className="gp-ai-scope">
      {live}
      {/* V117-1-10: contested laws waiting on a decision — the way into Scope › Decisions. */}
      <DecisionsEntry slug={slug} />
      <ObligationSourcesCard slug={slug} sources={s} grid={g} names={names} isAdmin={isAdmin} editorOpen={editor}
        releasesActive={isLegal ? (releases.data?.releases.filter((r) => r.status === 'active').length ?? null) : null}
        onEdit={() => setEditor((x) => !x)} onPropose={() => setPropose(true)} onRelease={() => setRelease({ preselect: null })}
        onReleases={() => setReleasesOpen(true)} onAskLegal={() => setAskLegal(true)} onAskPolicy={() => setAskPolicy(true)} />
      {editor && isLegal ? (
        <DecisionEditor slug={slug} sources={s} grid={g} onClose={() => { setEditor(false); focusLater('ai-sources-h'); }}
          onOpenCell={openCell}
          onDone={(m, close) => void done(m, close ? () => { setEditor(false); focusLater('ai-sources-h'); } : undefined)} />
      ) : null}

      <section className="gp-ai-card" aria-labelledby="ai-grid-h">
        <div className="gp-ai-card-head">
          <h3 id="ai-grid-h" className="gp-card-h">Applicability</h3>
          <span className="gp-sec-count">
            {g.counts.open ? <><b>{g.counts.open}</b>{` open${g.counts.waiting_on_owner ? ` · ${g.counts.waiting_on_owner} waiting` : ''}`}</>
              : g.decision ? `All decided · v${g.decision.version}${g.decision.state === 'draft' ? ' draft' : ''}` : ''}
          </span>
        </div>
        {!g.legislation ? (
          locked ? (
            <EmptyState title="AI legislation · Off · Locked by org policy" action={s.can_change_policy && s.org_slug
              ? <Link to={`/o/${encodeURIComponent(s.org_slug)}/settings`}>Change org policy</Link>
              : s.policy_contact ? <Button onClick={() => setAskPolicy(true)}>{`Ask ${contact}`}</Button> : <Link to={`/w/${slug}/app/today`}>Back to Today</Link>} />
          ) : (
            <EmptyState title="AI legislation is not tracked yet" action={isLegal ? <Button variant="primary" onClick={() => setEditor(true)}>Choose jurisdictions</Button>
              : isAdmin ? <Button onClick={() => setPropose(true)}>Propose a change</Button>
              : legalName ? <Button onClick={() => setAskLegal(true)}>{`Ask ${legalName}`}</Button>
              : <Link to={`/w/${slug}/app/today`}>Back to Today</Link>} />
          )
        ) : !g.systems.length ? (
          <EmptyState title="No systems in the AI Regulatory boundary" action={<a href="#boundary">Add systems below</a>} />
        ) : !g.laws.length ? (
          <EmptyState title="No rules on file for these jurisdictions yet" action={isLegal ? <Button onClick={() => setEditor(true)}>Change jurisdictions</Button> : <Link to={`/w/${slug}/app/today`}>Back to Today</Link>} />
        ) : (
          <ApplicabilityGrid grid={g} names={names} isLegal={isLegal} viewerId={viewerId} onOpenCell={(id) => openCell(id, cellButtonId(id))} />
        )}
      </section>

      <div id="boundary"><BoundaryPanel slug={slug} program={card.program} card={card} canManage={canManage} /></div>

      <CellEditor slug={slug} grid={g} cell={cell} isLegal={isLegal} viewerId={viewerId} names={names} legalName={legalName} open={!!cell}
        onClose={closeCell} onSaved={(m) => { closeCell(); void done(m); }}
        onRelease={isLegal ? (id) => { closeCell(); setRelease({ preselect: id }); } : undefined} />
      {s.can_propose ? (
        <ProposeDialog slug={slug} open={propose} sources={s} onClose={() => setPropose(false)} onDone={(m) => { setPropose(false); void done(m); }} />
      ) : null}
      <AskLegalDialog slug={slug} open={askLegal} grid={g} legalName={legalName} onClose={() => setAskLegal(false)} onDone={(m) => { setAskLegal(false); void done(m); }} />
      <AskPolicyDialog slug={slug} open={askPolicy} contact={contact} onClose={() => setAskPolicy(false)} onDone={(m) => { setAskPolicy(false); void done(m); }} />
      {isLegal ? (
        <>
          <ReleaseDialog slug={slug} open={!!release} grid={g} preselect={release?.preselect ?? null} onClose={() => setRelease(null)}
            onDone={(m) => { setRelease(null); void done(m); }} />
          <ReleasesDialog slug={slug} open={releasesOpen} releases={releases.data?.releases ?? []} onClose={() => setReleasesOpen(false)}
            onDone={(m) => { setReleasesOpen(false); void done(m); }}
            onNew={g.cells.some((c) => c.determination?.state === 'recorded') ? () => { setReleasesOpen(false); setRelease({ preselect: null }); } : null} />
        </>
      ) : null}
    </div>
  );
}

export function AiOperate({ slug, prefix, card }: { slug: string; prefix: string; card: ProgramCard }) {
  const grid = useAiGrid(slug);
  const blockers = useQuery({ queryKey: ['blockers', slug], queryFn: () => listBlockers(slug), staleTime: 30_000, retry: false });
  const split = useMemo(() => (grid.data ? operateSplit(grid.data) : null), [grid.data]);
  if (grid.isLoading) return <Spinner label="Loading obligations in force" />;
  if (grid.error || !grid.data || !split) return <Alert variant="error"><p>Obligations in force could not load.</p><Button onClick={() => void grid.refetch()}>Retry</Button></Alert>;
  const g = grid.data;
  const scope = `${prefix}/programs/ai-regulatory/scope`;
  // T4-H1: non-compliant = open remediation in the program that isn't an undecided cell (the count is the
  // program's, whoever looks; the rows are the ones this viewer may see).
  const nonCompliant = (blockers.data ?? []).filter((b) => b.next_action === 'remediate' && b.programs.includes(card.profile_id) && !b.item_key.startsWith('determination:'));
  const ncCount = card.ai_legal?.non_compliant ?? nonCompliant.length;
  const row = (c: AiGrid['cells'][number], action: string) => {
    const law = g.laws.find((l) => l.law_key === c.law_key)!;
    return (
      <li key={c.cell_id}>
        <span className="gp-ai-gap-t">{cellTitle(g, c)}</span>
        <span className="gp-dim">{law.effective_date ? `In force since ${fmtDay(law.effective_date, true)}` : 'In force'}</span>
        {c.state === 'interim' ? <Chip text="Interim position on file" tone="ok" /> : c.state === 'rule_changed' ? <Chip text="Rule changed" tone="amber" /> : c.fact_waiting ? <Chip text="Waiting on the owner" tone="amber" /> : null}
        <Link className="gp-linkbtn gp-linkbtn--compact" to={`${scope}?cell=${encodeURIComponent(c.cell_id)}`} aria-label={`${action} · ${cellTitle(g, c)}`}>{action}</Link>
      </li>
    );
  };
  return (
    <div className="gp-ai-scope">
      <section className="gp-ai-card" aria-labelledby="ai-gaps-h">
        <div className="gp-ai-card-head"><h3 id="ai-gaps-h" className="gp-card-h">{`Undecided in force · ${g.counts.undecided_in_force}`}</h3></div>
        {split.gaps.length ? <ul className="gp-ai-gaps" aria-labelledby="ai-gaps-h">{split.gaps.map((c) => row(c, 'Decide'))}</ul>
          : <p className="gp-dim">Every law in force is decided or has an interim position.</p>}
      </section>
      <section className="gp-ai-card" aria-labelledby="ai-nc-h">
        <div className="gp-ai-card-head"><h3 id="ai-nc-h" className="gp-card-h">{`Non-compliant · ${ncCount}`}</h3></div>
        {nonCompliant.length ? (
          <ul className="gp-ai-gaps" aria-labelledby="ai-nc-h">
            {nonCompliant.map((b) => (
              <li key={b.item_key}>
                <span className="gp-ai-gap-t">{b.title}</span>
                <span className="gp-dim">{b.holders.join(', ') || 'Unassigned'}</span>
                {b.overdue ? <Chip text="Overdue" tone="red" /> : null}
                <Link className="gp-linkbtn gp-linkbtn--compact" to={itemHref(prefix, b.ref ?? { kind: 'work_item', id: b.item_key })} aria-label={`Open · ${b.title}`}>Open</Link>
              </li>
            ))}
          </ul>
        ) : <p className="gp-dim">{ncCount ? 'With other owners' : 'Nothing open'}</p>}
      </section>
      {split.interim.length ? (
        <section className="gp-ai-card" aria-labelledby="ai-interim-h">
          <div className="gp-ai-card-head"><h3 id="ai-interim-h" className="gp-card-h">{`Interim positions · ${split.interim.length}`}</h3></div>
          <ul className="gp-ai-gaps" aria-labelledby="ai-interim-h">{split.interim.map((c) => row(c, 'Decide'))}</ul>
        </section>
      ) : null}
      <section className="gp-ai-card" aria-labelledby="ai-covered-h">
        <div className="gp-ai-card-head"><h3 id="ai-covered-h" className="gp-card-h">{`Applies and in force · ${split.covered.length}`}</h3></div>
        {split.covered.length ? (
          <ul className="gp-ai-gaps" aria-labelledby="ai-covered-h">
            {split.covered.map((c) => {
              const law = g.laws.find((l) => l.law_key === c.law_key)!;
              return (
                <li key={c.cell_id}>
                  <span className="gp-ai-gap-t">{cellTitle(g, c)}</span>
                  <span>{cellDisplay(c, law).label}</span>
                  {/* Medium: filtered to this law × system. */}
                  <Link className="gp-linkbtn gp-linkbtn--compact" aria-label={`Obligations · ${cellTitle(g, c)}`}
                    to={`${prefix}/systems/${encodeURIComponent(c.system_id)}?tab=requirements&law=${encodeURIComponent(c.law_key)}`}>Obligations</Link>
                </li>
              );
            })}
          </ul>
        ) : <EmptyState title="Nothing in force applies yet" action={<Link to={scope}>Review scope</Link>} />}
      </section>
    </div>
  );
}

export function AiConformity({ slug, prefix }: { slug: string; prefix: string }) {
  const grid = useAiGrid(slug);
  if (grid.isLoading) return <Spinner label="Loading conformity" />;
  if (grid.error || !grid.data) return <Alert variant="error"><p>Conformity could not load.</p><Button onClick={() => void grid.refetch()}>Retry</Button></Alert>;
  const c = grid.data.conformity;
  const names = new Map(grid.data.systems.map((s) => [s.system_id, s.name]));
  const scope = `${prefix}/programs/ai-regulatory/scope`;
  // T4-H10: the empty state names the state and the one action.
  if (c.status === 'not_applicable') return <EmptyState title="No EU high-risk systems" action={<Link to={scope}>Review EU determinations</Link>} />;
  return (
    <section className="gp-ai-card" aria-labelledby="ai-conf-h">
      <div className="gp-ai-card-head"><h3 id="ai-conf-h" className="gp-card-h">{c.status === 'prohibited' ? 'Prohibited practice' : 'Conformity assessment'}</h3></div>
      <ul className="gp-ai-gaps" aria-labelledby="ai-conf-h">
        {c.systems.map((s) => (
          <li key={s}><span className="gp-ai-gap-t">{names.get(s) ?? s}</span><Chip text={c.status === 'prohibited' ? 'Stop EU placement' : 'EU high-risk · provider'} tone="amber" /></li>
        ))}
      </ul>
      <div className="gp-actions"><Link className="gp-linkbtn gp-linkbtn--compact" to={scope}>Review EU determinations</Link></div>
    </section>
  );
}
